<template>
  <div>
    <div class="page-toolbar">
      <div class="page-title">小程序用户管理</div>
      <div style="display:flex; gap: 10px;">
        <el-input
          v-model.trim="keyword"
          placeholder="按用户ID/公司/手机号筛选"
          clearable
          style="width: 260px;"
        />
        <el-button icon="el-icon-refresh" @click="loadUsers">刷新</el-button>
      </div>
    </div>

    <el-card class="page-card">
      <el-table :data="filteredUsers" v-loading="loading" stripe>
        <el-table-column type="index" width="56" label="#" />
        <el-table-column label="用户" min-width="240">
          <template slot-scope="{ row }">
            <div class="user-cell">
              <el-avatar :size="34" :src="row.avatar || ''" icon="el-icon-user-solid" />
              <div>
                <div class="u-name">{{ formatUserCode(row) }}</div>
                <div class="u-id">{{ row.companyName || '-' }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="用户ID" width="110">
          <template slot-scope="{ row }">
            <span>{{ formatUserCode(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="contactPhone" label="手机号" width="140" />
        <el-table-column label="资质状态" width="120">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="qualificationTagType(row.qualificationStatus)">
              {{ qualificationStatusLabel(row.qualificationStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="是否供应商" width="110">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.supplier ? 'success' : 'info'">
              {{ row.supplier ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="保证金余额" width="140">
          <template slot-scope="{ row }">
            <span :class="depositAmountClass(row.depositBalanceFen)">{{ formatAmountFen(row.depositBalanceFen) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.blacklisted ? 'danger' : 'success'">
              {{ row.blacklisted ? '已拉黑' : '正常' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="updatedAt" label="更新时间" width="170" />
        <el-table-column label="操作" width="320" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="viewUser(row)">查看</el-button>
            <el-button size="mini" type="warning" :disabled="!Number(row.depositBalanceFen)" @click="handleDepositDeduct(row)">扣保证金</el-button>
            <el-button
              size="mini"
              :type="row.blacklisted ? 'success' : 'danger'"
              @click="toggleBlacklist(row)"
            >
              {{ row.blacklisted ? '取消拉黑' : '拉黑' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-drawer
      title="用户详情"
      :visible.sync="drawerVisible"
      size="460px"
      direction="rtl"
    >
      <div v-if="currentUser" class="detail-wrap">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="用户ID">{{ formatUserCode(currentUser) }}</el-descriptions-item>
          <el-descriptions-item label="头像">
            <el-image
              v-if="currentUser.avatar"
              :src="currentUser.avatar"
              :preview-src-list="[currentUser.avatar]"
              fit="cover"
              class="detail-avatar"
            />
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="公司">{{ currentUser.companyName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ currentUser.contactPhone || '-' }}</el-descriptions-item>
          <el-descriptions-item label="资质状态">{{ qualificationStatusLabel(currentUser.qualificationStatus, '-') }}</el-descriptions-item>
          <el-descriptions-item label="供应商">{{ currentUser.supplier ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="保证金余额">{{ formatAmountFen(currentUser.depositBalanceFen) }}</el-descriptions-item>
          <el-descriptions-item label="拉黑">{{ currentUser.blacklisted ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="最近登录">{{ currentUser.lastLoginTime || '-' }}</el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ currentUser.createdAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="更新时间">{{ currentUser.updatedAt || '-' }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { deductUserDeposit, fetchUserDetail, fetchUsers, setUserBlacklist } from '../../api/admin'
import { isAuthRedirectError } from '../../utils/auth'

export default {
  name: 'UserListView',
  data() {
    return {
      loading: false,
      users: [],
      keyword: '',
      drawerVisible: false,
      currentUser: null
    }
  },
  computed: {
    filteredUsers() {
      const key = String(this.keyword || '').toLowerCase()
      if (!key) return this.users
      return this.users.filter((u) => {
        const userCode = this.formatUserCode(u).toLowerCase()
        const companyName = String(u.companyName || '').toLowerCase()
        const contactPhone = String(u.contactPhone || '').toLowerCase()
        return userCode.includes(key) || companyName.includes(key) || contactPhone.includes(key)
      })
    }
  },
  created() {
    this.loadUsers()
  },
  methods: {
    formatUserCode(user) {
      const directCode = String((user && user.userCode) || '').trim()
      if (directCode) return directCode
      const userId = Number(user && user.userId)
      if (!Number.isInteger(userId) || userId <= 0) return '--'
      return userId < 1000 ? String(userId).padStart(3, '0') : String(userId)
    },
    qualificationTagType(status) {
      if (status === 'approved') return 'success'
      if (status === 'rejected') return 'danger'
      if (status === 'pending') return 'warning'
      return 'info'
    },
    qualificationStatusLabel(status, fallback = '未认证') {
      if (status === 'approved') return '已通过'
      if (status === 'rejected') return '已驳回'
      if (status === 'pending') return '待审核'
      if (status === 'none') return '未认证'
      return status || fallback
    },
    formatAmountFen(amountFen) {
      const amount = Number(amountFen)
      if (!Number.isFinite(amount) || amount <= 0) {
        return '¥0.00'
      }
      return `¥${(amount / 100).toFixed(2)}`
    },
    depositAmountClass(amountFen) {
      return Number(amountFen) > 0 ? 'deposit-amount-ok' : 'deposit-amount-empty'
    },
    async loadUsers() {
      this.loading = true
      try {
        this.users = await fetchUsers()
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '用户列表获取失败')
      } finally {
        this.loading = false
      }
    },
    async viewUser(row) {
      try {
        this.currentUser = await fetchUserDetail(row.openid)
        this.drawerVisible = true
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '用户详情获取失败')
      }
    },
    toggleBlacklist(row) {
      const action = row.blacklisted ? '取消拉黑' : '拉黑'
      this.$confirm(`确认${action}该用户吗？`, '提示', { type: 'warning' })
        .then(async () => {
          await setUserBlacklist(row.openid, !row.blacklisted)
          this.$message.success('操作成功')
          await this.loadUsers()
          if (this.currentUser && this.currentUser.openid === row.openid) {
            this.currentUser = await fetchUserDetail(row.openid)
          }
        })
        .catch(() => {})
    },
    handleDepositDeduct(row) {
      const currentBalanceFen = Math.max(0, Number(row.depositBalanceFen) || 0)
      const currentBalanceYuan = (currentBalanceFen / 100).toFixed(2)
      this.$prompt('请输入扣除金额（元）', '扣保证金', {
        inputValue: '',
        inputPlaceholder: `当前可扣 ${currentBalanceYuan} 元`,
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        inputValidator: (value) => {
          const normalized = String(value || '').trim()
          if (!/^(\d+)(\.\d{1,2})?$/.test(normalized)) {
            return false
          }
          const amountFen = Math.round(Number(normalized) * 100)
          return amountFen > 0 && amountFen <= currentBalanceFen
        },
        inputErrorMessage: '请输入大于 0 且不超过当前保证金余额的金额，最多两位小数'
      })
        .then(async ({ value }) => {
          const amountFen = Math.round(Number(value) * 100)
          await deductUserDeposit(row.openid, amountFen)
          this.$message.success('扣除保证金成功')
          await this.loadUsers()
          if (this.currentUser && this.currentUser.openid === row.openid) {
            this.currentUser = await fetchUserDetail(row.openid)
          }
        })
        .catch((error) => {
          if (error === 'cancel' || error === 'close') {
            return
          }
          this.$message.error(error.message || '扣除保证金失败')
        })
    }
  }
}
</script>

<style scoped>
.user-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.u-name {
  font-size: 14px;
  font-weight: 600;
}

.u-id {
  margin-top: 2px;
  color: #7d88a3;
  font-size: 12px;
}

.detail-wrap {
  padding: 0 8px;
}

.detail-avatar {
  width: 88px;
  height: 88px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
}

.deposit-amount-ok {
  color: #15803d;
  font-weight: 600;
}

.deposit-amount-empty {
  color: #94a3b8;
}
</style>
